import { Router } from '@angular/router';
import { Injectable, Injector } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AsyncSubject, BehaviorSubject, Observable, firstValueFrom, forkJoin } from 'rxjs';
import { MEError, MEErrorType } from '../../error/me-error';
import { MEAPIResponse, MECustomTitleErrorResponseHandler, MEDoNothingResponseHandler, MEEntityAPIResponseHandler, ResponseCode, } from '../../services/api/me-api-response';
import { MEAPIService } from '../../services/api/me-api.service';
import { MFADialogConfig, MfaComponent } from './mfa.component';
import { MEErrorService } from '../../error/me-error.service';
import { ExternalSettingsService } from '../../services/external-settings/external-settings.service';
import { UserService } from '../../services/user/user.service';
import { InternalSettingsService } from '../../services/internal-settings/internal-settings.service';
import { MFAResult, MFAScope, TfaAuthRequest, TfaPreauthRequest, TokenResponseBean } from './mfa-bean';
import { MeConfirmDialogService } from '../me-dialog/me-confirm/me-confirm.service';
import { ConfirmDialogConfig, DialogMessageStyle, DialogType } from '../me-dialog/me-confirm/me-confirm.component';
import { MeSuccessDialogService } from '../me-dialog/me-success/me-success.service';
import { CustomDialogWidthConfig, ResponsiveDesignService } from '../../services/responsive-design/responsive-design.service';
import { SecureStorageService, SecureStorageType } from 'src/app/system/utility/secure-storage.service';
import { AppAuthenticatorStoredCredential, AppAuthenticatorResult } from '../app-authenticator/app-authenticator-bean';
import { AppAuthenticatorNeedsActivateChoice, AppAuthenticatorNeedsActivateComponent, AppAuthenticatorNeedsActivateDialogData} from '../app-authenticator/app-authenticator-needs-activate/app-authenticator-needs-activate.component';
import { AppAuthenticatorWaitingComponent, AppAuthenticatorWaitingDialogData} from '../app-authenticator/app-authenticator-waiting/app-authenticator-waiting.component';
import { AppAuthenticatorNoNotificationChoice, AppAuthenticatorNoNotificationComponent} from '../app-authenticator/app-authenticator-no-notification/app-authenticator-no-notification.component';
import { AppAuthenticatorResetPasswordChoice, AppAuthenticatorResetPasswordComponent} from '../app-authenticator/app-authenticator-reset-password/app-authenticator-reset-password.component';
import { AppAuthenticatorService } from '../app-authenticator/app-authenticator.service';
import { BiometricChallenge, BiometricResultStatus, BiometricService } from 'src/app/system/utility/biometric.service';
import { CryptoService } from 'src/app/system/utility/crypto.service';
import { logger } from '../../services/logging/logging.service';
import { PlatformService } from 'src/app/system/utility/platform.service';
import { PushNotificationService } from 'src/app/system/utility/push-notification.service';
import { StringUtilityService } from 'src/app/system/utility/string-utility.service';

const APP_AUTH_POLL_INTERVAL_MS = 5000;
/** User MFA destination value that indicates App Authenticator (tfaDestination / mfaDestination). */
const TFA_DESTINATION_APP_AUTHENTICATOR = 8;

@Injectable({
  providedIn: 'root',
})
export class MfaService {

  private API_PREAUTH_MFA = MEAPIService.BASE_URL + '/customer/tfa/preauth';
  private API_VERIFY_MFA = MEAPIService.BASE_URL + '/customer/tfa/auth';
  public static readonly API_APP_AUTH_PROCESS = MEAPIService.BASE_URL + '/customer/tfa/appAuth';
  private API_APP_AUTH_REJECT = MEAPIService.BASE_URL + '/customer/tfa/appAuthReject';
  private API_APP_AUTH_APPROVE = MEAPIService.BASE_URL + '/customer/tfa/appAuthApprove';
  public result!: BehaviorSubject<MFAResult>;
  private authResult: any;

  private isExternalMfa: boolean = false;
  private forceMfaOnTrustedMobiBankingDevices = false;
  private isModuleEnabledPushNotifications = false;
  /** Whether MFA should use App Authenticator (user.tfaDestination / profile.mfaDestination === 8). */
  public defaultAppAuthenticator: boolean = false;

  public scope!: MFAScope;
  public token: string = '';
  public status: ResponseCode = ResponseCode.SUCCESS;
  public dialogRef!: MatDialogRef<MfaComponent, null>;

  private additionalProps: {} = {};
  private appAuthPolling = false;
  private appAuthPollTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private useAppAuthenticator: boolean = false;
  /** When true, next MFA_AUTH_APP response restarts polling without reopening the waiting dialog. */
  private suppressAppAuthWaitingDialog = false;
  /** When true, preauth was triggered by App Auth "Resend" — never fall through to standard MFA UI. */
  private pendingAppAuthResend = false;
  /**
   * When true, cancel / App Auth errors must not log the user off
   * (mid-session / transactional flows such as EFT).
   */
  private keepSessionOnCancel = false;
  /** Scope / props / result to resume after mid-session App Authenticator activation MFA. */
  private scopeBeforeAppActivate: MFAScope | null = null;
  private additionalPropsBeforeAppActivate: {} | null = null;
  private resultBeforeAppActivate: BehaviorSubject<MFAResult> | null = null;
  /** RESET_PASSWORD shows its App Authenticator choice only once per challenge. */
  private resetPasswordAppAuthNotificationRequested = false;
  private needsActivateDialogRef!: MatDialogRef<AppAuthenticatorNeedsActivateComponent, AppAuthenticatorNeedsActivateChoice>;
  private appAuthWaitingDialogRef!: MatDialogRef<AppAuthenticatorWaitingComponent, void>;
  private appAuthNoNotificationDialogRef!: MatDialogRef<AppAuthenticatorNoNotificationComponent, AppAuthenticatorNoNotificationChoice>;
  private appAuthResetPasswordDialogRef!: MatDialogRef<AppAuthenticatorResetPasswordComponent, AppAuthenticatorResetPasswordChoice>;

  constructor(
    private srvAPI: MEAPIService,
    private srvDialog: MatDialog,
    private translate: TranslateService,
    private srvError: MEErrorService,
    private srvConfirm: MeConfirmDialogService,
    private srvSuccess: MeSuccessDialogService,
    private srvES: ExternalSettingsService,
    private srvUser: UserService,
    private srvIS: InternalSettingsService,
    private srvRpDesign: ResponsiveDesignService,
    private router: Router,
    private srvSecureStorage: SecureStorageService,
    private injector: Injector,
  ) {
    forkJoin([
      this.srvES.externalSettings$,
    ]).subscribe(([externalSettings]) => {
      this.isExternalMfa = externalSettings.globalVariables.isModuleEnabledExternalMfa;
      this.forceMfaOnTrustedMobiBankingDevices = externalSettings.globalVariables.forceMfaOnTrustedMobiBankingDevices;
      this.isModuleEnabledPushNotifications = externalSettings.globalVariables.isModuleEnabledPushNotifications;
    });

    this.srvIS.internalSettings$.subscribe((internalSettings) => {
      this.defaultAppAuthenticator = internalSettings?.profile?.mfaDestination === TFA_DESTINATION_APP_AUTHENTICATOR;
      this.useAppAuthenticator = this.defaultAppAuthenticator;
    });
  }

  public useStandardMFA(): void {
    this.useAppAuthenticator = false;
  }

  public useAppAuthenticatorDevice(): void {
    this.useAppAuthenticator = true;
  }

  /**
   * Mid-session App Authenticator activation: keep the user logged in on cancel/error,
   * and remember the original MFA scope/props/result so the transaction can resume.
   */
  public beginTransactionalAppAuthActivation(): void {
    this.keepSessionOnCancel = true;
    this.scopeBeforeAppActivate = this.scope;
    this.additionalPropsBeforeAppActivate = { ...this.additionalProps};
    this.resultBeforeAppActivate = this.result ?? null;
  }

  public clearTransactionalAppAuthActivation(): void {
    this.keepSessionOnCancel = false;
    this.scopeBeforeAppActivate = null;
    this.additionalPropsBeforeAppActivate = null;
    this.resultBeforeAppActivate = null;
  }

  /** Notify both the current and (if any) pre-activation MFA result subjects. */
  public publishMfaResult(result: MFAResult): void {
    if (this.resultBeforeAppActivate && this.resultBeforeAppActivate !== this.result) {
      this.resultBeforeAppActivate.next(result);
    }
    if (this.result) {
      this.result.next(result);
    }
  }

  /** Whether cancel/error should end the session (login / reset / login-time activation only). */
  public shouldLogoffOnMfaCancelOrError(): boolean {
    if (this.keepSessionOnCancel) {
      return false;
    }
    switch (this.scope) {
      case MFAScope.LOGIN:
      case MFAScope.RESET_PASSWORD:
      case MFAScope.APP_AUTHENTICATE_ACTIVATE:
        return true;
      default:
        return false;
    }
  }

  /**
   * @param reuseResult When true, keep the existing result subject so callers
   * (e.g. after "Send MFA code" from App Auth wait) still receive VERIFIED/etc.
   */
  preauth(scope: MFAScope, additionalProps: {} = {}, reuseResult: boolean = false): BehaviorSubject<MFAResult> {
    this.scope = scope;
    this.additionalProps = additionalProps;
    if (!reuseResult || !this.result) {
      this.result = new BehaviorSubject<MFAResult>(MFAResult.RETRY);
    }

    let bean: TfaPreauthRequest = {
      scope: this.scope,
      external: this.isExternalMfa,
      useAppAuth: this.useAppAuthenticator
    };
    bean = Object.assign(bean, this.additionalProps);

    // Biometric change must always use standard MFA — never App Authenticator.
    if (this.scope === MFAScope.BIOMETRIC_CHANGE) {
      bean.useAppAuth = false;
    }

    if (this.defaultAppAuthenticator && !this.useAppAuthenticator) {
      this.useAppAuthenticator = true;
    }

    // Trigger the /customer/tfa/preauth endpoint
    this.srvAPI
      .Post<TokenResponseBean>(this.API_PREAUTH_MFA, bean, [
        new GenerateLoadHandler(ResponseCode.SUCCESS, this),
        new GenerateLoadHandler(ResponseCode.MFA_VALIDATION_REQUIRED, this),
        new MEDoNothingResponseHandler(ResponseCode.MFA_NOT_EXPIRED),
        new GenerateLoadHandler(ResponseCode.MFA_SWITCH_TO_INTERNAL, this),
        new GenerateLoadHandler(ResponseCode.MFA_VALIDATION_SKIPPED, this),
        new ExceedMaxRetryLogoffHandler(ResponseCode.MFA_EXCEED_MAX_RETRY_LOGOFF, this, bean.useAppAuth ?? false),
        new ExceedMaxRetrySuspendHandler(ResponseCode.MFA_EXCEED_MAX_RETRY_SUSPENSE, this, bean.useAppAuth ?? false),  
        new MFARegistrationHandler(ResponseCode.MFA_REGISTRATION_REQUIRED, this.srvConfirm, this.srvUser, this.translate),
        new AppAuthenticatorRequiredHandler(ResponseCode.MFA_AUTH_APP, this),
        new AppAuthenticatorSelfAuthHandler(ResponseCode.MFA_AUTH_APP_SELF, this),
        new AppAuthenticatorNeedsActivationHandler(ResponseCode.APP_AUTHENTICATOR_NEEDS_ACTIVATION, this),
      ])
      .subscribe((res) => {
        const wasAppAuthResend = this.pendingAppAuthResend;
        this.pendingAppAuthResend = false;
        // Clear resend suppress if preauth did not return MFA_AUTH_APP (handler already cleared it).
        this.suppressAppAuthWaitingDialog = false;
        if (res) {
          if (wasAppAuthResend) {
            // e.g. MFA_NOT_EXPIRED — stay on App Authenticator wait/poll, do not open standard MFA.
            this.startAppAuthPolling();
            return;
          }
          this.showDialog();
        }
      });
    return this.result;
  }

  public startAppAuthPolling(): void {
    this.appAuthPolling = true;
    this.appAuth();
  }

  public stopAppAuthPolling(): void {
    this.appAuthPolling = false;
    if (this.appAuthPollTimeoutId != null) {
      clearTimeout(this.appAuthPollTimeoutId);
      this.appAuthPollTimeoutId = null;
    }
  }

  public scheduleNextAppAuthPoll(): void {
    if (!this.appAuthPolling) {
      return;
    }
    this.appAuthPollTimeoutId = setTimeout(() => {
      this.appAuthPollTimeoutId = null;
      if (this.appAuthPolling) {
        this.appAuth();
      }
    }, APP_AUTH_POLL_INTERVAL_MS);
  }

  public appAuth(): void {
    this.srvSecureStorage.get(SecureStorageType.APP_AUTHENTICATOR_PRIVATE_KEY).subscribe((stored) => {
      let bean: Partial<TfaAuthRequest> = {
        token: this.token,
        scope: this.scope,
      };
      bean = Object.assign(bean, this.additionalProps);
      if (stored) {
        const credential: AppAuthenticatorStoredCredential = JSON.parse(stored);
        bean.userId = credential.userId;
      }

      this.srvAPI.Post(MfaService.API_APP_AUTH_PROCESS, bean, [
        new AppAuthSuccessHandler(ResponseCode.SUCCESS, this),
        new AppAuthInProgressHandler(ResponseCode.MFA_STILL_IN_PROGRESS, this),
        new AppAuthRejectedHandler(ResponseCode.APP_AUTHENTICATOR_REJECTED, this),
        new AppAuthExpiredHandler(ResponseCode.MFA_CODE_EXPIRED, this),
        new AccountMonitoringReviewMfaHandler(ResponseCode.ACCOUNT_MONITORING_REVIEW, this),
      ]).subscribe();
    });
  }

  /**
   * Approve an App Authenticator push request on the trusted device.
   * Success is a no-op here — the caller shows the approval success UI.
   * Do not use AppAuthSuccessHandler (that completes the polling-device MFA flow).
   */
  approve(tfaPassphrase?: string): AsyncSubject<boolean> {
    const result = new AsyncSubject<boolean>();
    this.srvSecureStorage.get(SecureStorageType.APP_AUTHENTICATOR_PRIVATE_KEY).subscribe((stored) => {
      const bean: TfaAuthRequest = {
        token: this.token,
        scope: this.scope,
        userResponse: tfaPassphrase,
      };
      if (stored) {
        const credential: AppAuthenticatorStoredCredential = JSON.parse(stored);
        bean.userId = credential.userId;
      }
      this.srvAPI.Post(this.API_APP_AUTH_APPROVE, bean, [
        new MEDoNothingResponseHandler(ResponseCode.SUCCESS),
        new AppAuthApproveExpiredHandler(ResponseCode.MFA_CODE_EXPIRED, this),
      ]).subscribe({
        next: (ok) => {
          result.next(ok);
          result.complete();
        },
        error: (err) => result.error(err),
      });
    });
    return result;
  }
  /**
   * MFA_AUTH_APP_SELF (1517): this device must self-authorize.
   * On login, prompt for push notifications when disabled, then biometrics + auth().
   */
  public async handleAppAuthenticatorSelfAuth(actionResult: string): Promise<void> {
    await this.reconcileOrphanedAppAuthBiometrics();
    await this.ensureNotificationsBeforeSelfAuth();
    await this.performAppAuthenticatorSelfAuth(actionResult);
  }

  /**
   * If App Authenticator is present (stored key or pending login keypair) but the device
   * has no biometrics, disable biometrics on the server and clear local storage before
   * continuing MFA_AUTH_APP_SELF.
   */
  private async reconcileOrphanedAppAuthBiometrics(): Promise<void> {
    const srvBioMetric = this.injector.get(BiometricService);
    const srvAppAuth = this.injector.get(AppAuthenticatorService);
    const [stored, status] = await Promise.all([
      firstValueFrom(this.srvSecureStorage.get(SecureStorageType.APP_AUTHENTICATOR_PRIVATE_KEY)),
      firstValueFrom(srvBioMetric.getPlatformBiometricStatus()),
    ]);

    // No need to disable if the user is using App Authenticator and authKey are available
    // But why ?
    const hasAppAuthenticator = !!(stored && stored.length > 0) || srvAppAuth.hasPendingLoginKeyPair();
    if (!hasAppAuthenticator || (status.isPlatformSupport && status.biometricType)) {
      return;
    }

    logger.debug('App Authenticator present but biometrics unavailable; disabling biometrics');
    try {
      await firstValueFrom(srvBioMetric.disableBiometricsForOrphanedAppAuth());
    } catch (err) {
      logger.error(err);
    }
  }

  /**
   * LOGIN self-auth on mobile: show the enable-notification dialog when OS permission
   * is not granted, then continue regardless of Enable / Dismiss.
   */
  private async ensureNotificationsBeforeSelfAuth(): Promise<void> {

    // Only require to check if Login & Mobile & Push Notifications are enabled
    if (this.scope !== MFAScope.LOGIN) return;
    if (!PlatformService.isMobile()) return;
    if (!this.isModuleEnabledPushNotifications) return;

    // Check if push notifications are granted
    const granted = await PushNotificationService.hasReceivePermission();
    if (granted) {
      return;
    }

    // Prompt to enable push notifications again
    const srvAppAuth = this.injector.get(AppAuthenticatorService);
    await srvAppAuth.promptEnableNotification();
  }

  /**
   * Prompt biometrics, decrypt actionResult with the stored private key, then verify via auth().
   */
  private async performAppAuthenticatorSelfAuth(actionResult: string): Promise<void> {
    // BiometricService / AppAuthenticatorService inject MfaService — resolve lazily to avoid circular DI.
    const srvBioMetric = this.injector.get(BiometricService);
    const srvAppAuth = this.injector.get(AppAuthenticatorService);

    const status = await firstValueFrom(srvBioMetric.getPlatformBiometricStatus());
    if (!status.isPlatformSupport || !status.biometricType) {
      logger.error('Biometrics not available for App Authenticator self-auth; falling back to standard MFA');
      srvAppAuth.clearPendingLoginKeyPair();
      this.sendStandardMfaFromAppAuthWait();
      return;
    }

    const challenge: BiometricChallenge = {
      biometricType: status.biometricType,
      description: srvBioMetric.getBiometricName(status.biometricType),
    };

    const bioResult = await firstValueFrom(srvBioMetric.performBiometric(challenge));
    if (bioResult !== BiometricResultStatus.SUCCESS) {
      return this.cancelAppAuthWait();
    }

    // Enforce Standard MFA if "ForceMFAOnTrustedMobiBankingDevices" is enabled
    const userId = (this.additionalProps as { userId?: string }).userId ?? '';
    if (this.scope === MFAScope.LOGIN && this.forceMfaOnTrustedMobiBankingDevices) {
      await srvAppAuth.commitPendingPrivateKey(userId);
      this.sendStandardMfaFromAppAuthWait();
      return;
    }

    if (!actionResult) {
      return this.failAppAuthenticatorSelfAuth("MFA_AUTH_APP_SELF missing actionResult");
    }

    // Why Commit again ? Suppose it was already saved in the secure storage during activation
    const committed = await srvAppAuth.commitPendingPrivateKey(userId);
    const stored = await firstValueFrom(this.srvSecureStorage.get(SecureStorageType.APP_AUTHENTICATOR_PRIVATE_KEY));
    if (!committed && !stored) {
      return this.failAppAuthenticatorSelfAuth("App Authenticator private key not committed for self-auth");
    }

    if (!stored) {
      return this.failAppAuthenticatorSelfAuth("App Authenticator private key not found for self-auth");
    }

    if (!StringUtilityService.isValidJSON(stored)) {
      return this.failAppAuthenticatorSelfAuth("App Authenticator private key is not a valid JSON");
    }

    let privateKey: string = JSON.parse(stored).privateKey;
    if (!privateKey) {
      return this.failAppAuthenticatorSelfAuth("App Authenticator private key is empty");
    }

    try {
      const tfaPhrase = await CryptoService.decryptCompactJwe(actionResult, privateKey);
      if (!tfaPhrase) {
        return this.failAppAuthenticatorSelfAuth("Error decrypting actionResult with private key");
      }

      // The actual send to server to verify TFA Phrase 
      this.auth(tfaPhrase).subscribe();

    } catch (err) {
      return this.failAppAuthenticatorSelfAuth(err);
    }
  }

  private failAppAuthenticatorSelfAuth(errorMsg: any = ''): void {
    if (errorMsg) {
      logger.error(errorMsg);
    }

    this.injector.get(AppAuthenticatorService).clearPendingLoginKeyPair();
    this.srvError.showDialog(
      this.translate.instant('APP_AUTHENTICATOR.SELF_AUTH_FAILED_TITLE'),
      this.translate.instant('APP_AUTHENTICATOR.SELF_AUTH_FAILED')
    ).subscribe(() => this.cancelAppAuthWait());
  }

  /**
   * Waiting dialog while App Authenticator push is pending.
   * Stays open until poll returns a non-MFA_STILL_IN_PROGRESS result, or the user acts.
   */
  public showAppAuthWaitingDialog(): void {
    if (this.suppressAppAuthWaitingDialog) {
      this.suppressAppAuthWaitingDialog = false;
      return;
    }

    if (this.appAuthWaitingDialogRef?.getState() === MatDialogState.OPEN) {
      return;
    }

    const data: AppAuthenticatorWaitingDialogData = {
      onStillNotGettingNotification: () => this.showAppAuthNoNotificationDialog(),
    };
    const dialogConfig = new MatDialogConfig<AppAuthenticatorWaitingDialogData>();
    dialogConfig.data = data;
    dialogConfig.maxWidth = 600;
    dialogConfig.width = '98%';
    dialogConfig.height = 'auto';
    dialogConfig.panelClass = 'custom-dialog-confirm-messages';
    dialogConfig.disableClose = true;

    this.appAuthWaitingDialogRef = this.srvDialog.open(AppAuthenticatorWaitingComponent, dialogConfig);
  }

  /**
   * Options when the user is not receiving the App Authenticator push:
   * resend notification (preauth again, keep waiting dialog) or send a standard MFA code.
   */
  public showAppAuthNoNotificationDialog(): void {
    if (this.appAuthNoNotificationDialogRef?.getState() === MatDialogState.OPEN) {
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.maxWidth = 600;
    dialogConfig.width = '98%';
    dialogConfig.height = 'auto';
    dialogConfig.panelClass = 'custom-dialog-confirm-messages';
    dialogConfig.disableClose = true;

    this.appAuthNoNotificationDialogRef = this.srvDialog.open(AppAuthenticatorNoNotificationComponent, dialogConfig);
    this.appAuthNoNotificationDialogRef.afterClosed().subscribe((choice) => {
      switch (choice) {
        case 'resend':
          this.resendAppAuthNotification();
          break;
        case 'sendCode':
          this.stopAppAuthPolling();
          this.closeAppAuthWaitingDialog();
          this.sendStandardMfaFromAppAuthWait();
          break;
        case 'cancel':
          this.cancel();
          break;
        default:
          break;
      }
    });
  }

  /**
   * Call preauth again for App Authenticator push without reopening the waiting dialog
   * and without falling through to the standard MFA code-entry dialog.
   */
  private resendAppAuthNotification(): void {
    this.stopAppAuthPolling();
    this.useAppAuthenticatorDevice();
    this.suppressAppAuthWaitingDialog = true;
    this.pendingAppAuthResend = true;
    this.preauth(this.scope, { ...this.additionalProps, useAppAuth: true}, true);
  }

  /**
   * Fall back from App Authenticator push wait to standard MFA code entry,
   * preserving the original preauth result subject for the caller.
   */
  private sendStandardMfaFromAppAuthWait(): void {
    this.resetPasswordAppAuthNotificationRequested = false;
    this.useStandardMFA();
    const props = { ...this.additionalProps, useAppAuth: false};
    this.preauth(this.scope, props, true);
  }

  public closeAppAuthWaitingDialog(): void {
    this.closeAppAuthNoNotificationDialogIfOpen();
    if (this.appAuthWaitingDialogRef?.getState() === MatDialogState.OPEN) {
      this.appAuthWaitingDialogRef.close();
    }
  }

  public closeAppAuthNoNotificationDialogIfOpen(): void {
    if (this.appAuthNoNotificationDialogRef?.getState() === MatDialogState.OPEN) {
      this.appAuthNoNotificationDialogRef.close();
    }
  }

  /**
   * Brief success dialog after App Authenticator push is approved (polling device).
   * Closes automatically after 2 seconds, then the caller can continue.
   */
  public showAppAuthApprovedSuccessDialog(): Observable<null | undefined> {
    return this.srvSuccess.showAutoClose(
      this.translate.instant('APP_AUTHENTICATOR.AUTHENTICATED_TITLE'),
      this.translate.instant('APP_AUTHENTICATOR.AUTHENTICATED_MESSAGE'),
      2000
    );
  }

  /**
   * Complete a successful App Authenticator challenge.
   * When the waiting dialog was open (polling device), show the brief approved dialog first.
   */
  public completeAppAuthSuccess(response: MEAPIResponse<TokenResponseBean>): void {
    const showApprovedDialog = this.appAuthWaitingDialogRef?.getState() === MatDialogState.OPEN;

    this.stopAppAuthPolling();
    this.resetPasswordAppAuthNotificationRequested = false;
    this.setAuthResult(response);
    this.closeAppAuthWaitingDialog();
    this.closeDialogIfOpen();

    if (showApprovedDialog) {
      this.showAppAuthApprovedSuccessDialog().subscribe(() => {
        if (this.result) {
          this.result.next(MFAResult.VERIFIED);
        }
      });
      return;
    }

    if (this.result) {
      this.result.next(MFAResult.VERIFIED);
    }
  }

  /**
   * MFA_CODE_EXPIRED (2046) from push approval (appAuthApprove).
   */
  public handlePushApprovalExpired(): void {
    this.injector.get(AppAuthenticatorService).handlePushApprovalExpired();
  }

  /**
   * MFA_CODE_EXPIRED (2046) while polling for App Authenticator approval.
   */
  public showAppAuthExpiredDialog(): void {
    const config: ConfirmDialogConfig = {
      dialogType: DialogType.REJECT,
      title: this.translate.instant('APP_AUTHENTICATOR.POLL_EXPIRED_TITLE'),
      message: this.translate.instant('APP_AUTHENTICATOR.POLL_EXPIRED_MESSAGE'),
      messageStyle: DialogMessageStyle.LEFT_ALIGN,
      confirmButtonText: this.translate.instant('BUTTON_OK'),
    };

    this.srvConfirm.showDialog(config).subscribe(() => {
      this.closeDialogIfOpen();
      this.publishMfaResult(MFAResult.EXPIRED);
      this.clearTransactionalAppAuthActivation();

      if (this.scope === MFAScope.LOGIN) {
        this.srvUser.logoff().subscribe(() => {
          window.location.reload();
        });
      }
    });
  }

  /**
   * Shown when App Authenticator push is rejected on the trusted device (4316).
   */
  public showAppAuthRejectedDialog(): void {
    const rejectedMessageKey = this.scope === MFAScope.RESET_PASSWORD
      ? 'APP_AUTHENTICATOR.REJECTED_MESSAGE_RESET_PASSWORD'
      : 'APP_AUTHENTICATOR.REJECTED_MESSAGE';
    const config: ConfirmDialogConfig = {
      dialogType: DialogType.REJECT,
      title: this.translate.instant('APP_AUTHENTICATOR.REJECTED_TITLE'),
      message: this.translate.instant(rejectedMessageKey),
      messageStyle: DialogMessageStyle.LEFT_ALIGN,
      confirmButtonText: this.translate.instant('BUTTON_OK'),
    };

    this.srvConfirm.showDialog(config).subscribe(() => {
      this.cancelAppAuthWait();
    });
  }

  /**
   * Shown on the trusted device after appAuthReject succeeds.
   */
  public showAppAuthDenySuccessDialog(): Observable<boolean | null | undefined> {
    const config: ConfirmDialogConfig = {
      dialogType: DialogType.REJECT,
      title: this.translate.instant('APP_AUTHENTICATOR.DENY_SUCCESS_TITLE'),
      message: this.translate.instant('APP_AUTHENTICATOR.DENY_SUCCESS_MESSAGE'),
      messageStyle: DialogMessageStyle.LEFT_ALIGN,
      confirmButtonText: this.translate.instant('BUTTON_OK'),
    };
    return this.srvConfirm.showDialog(config);
  }

  /**
   * APP_AUTHENTICATOR_NEEDS_ACTIVATION (4317) from preauth.
   * LOGIN → start App Authenticator activation.
   * Other scopes → browser/app choice dialog (Back / Send Me a Code / Continue on app).
   */
  public handleAppAuthenticatorNeedsActivation(userId: string = ''): void {
    if (this.scope === MFAScope.LOGIN) {
      this.startAppAuthenticatorActivation(userId);
      return;
    }

    this.showNeedsActivateDialog(userId);
  }

  /**
   * Show the RESET_PASSWORD App Authenticator or standard MFA choice.
   * When additionalProps is provided, prepares MFA state and returns the result
   * subject without calling preauth (preauth runs after the user chooses).
   */
  public showResetPasswordAppAuthDialog(additionalProps?: {}): BehaviorSubject<MFAResult> {
    if (additionalProps !== undefined) {
      this.scope = MFAScope.RESET_PASSWORD;
      this.additionalProps = additionalProps;
      this.result = new BehaviorSubject<MFAResult>(MFAResult.RETRY);
      this.useAppAuthenticatorDevice();
      this.resetPasswordAppAuthNotificationRequested = false;
    }

    if (this.appAuthResetPasswordDialogRef?.getState() === MatDialogState.OPEN) {
      return this.result;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.maxWidth = 600;
    dialogConfig.width = '98%';
    dialogConfig.height = 'auto';
    dialogConfig.panelClass = 'custom-dialog-confirm-messages';
    dialogConfig.disableClose = true;

    this.appAuthResetPasswordDialogRef = this.srvDialog.open(AppAuthenticatorResetPasswordComponent, dialogConfig);
    this.appAuthResetPasswordDialogRef.afterClosed().subscribe((choice) => {
      switch (choice) {
        case 'sendNotification':
          this.showAppAuthNotification();
          break;
        case 'sendCode':
          this.sendStandardMfaFromAppAuthWait();
          break;
        case 'cancel':
          this.cancel();
          break;
        default:
          break;
      }
    });

    return this.result;
  }

  /**
   * Send an App Authenticator push. The next MFA_AUTH_APP response opens the
   * existing waiting dialog instead of showing the choice again.
   */
  public showAppAuthNotification(): void {
    this.resetPasswordAppAuthNotificationRequested = true;
    this.useAppAuthenticatorDevice();
    this.preauth(this.scope, { ...this.additionalProps, useAppAuth: true}, true);
  }

  /**
   * Handle MFA_AUTH_APP from preauth. RESET_PASSWORD initially asks whether
   * the App Authenticator is ready; subsequent responses use the normal wait flow.
   */
  public handleAppAuthenticatorRequired(): void {
    this.injector.get(AppAuthenticatorService).clearPendingLoginKeyPair();

    if (
      this.scope === MFAScope.RESET_PASSWORD
      && !this.resetPasswordAppAuthNotificationRequested
    ) {
      this.showResetPasswordAppAuthDialog();
      return;
    }

    this.showAppAuthWaitingDialog();
    this.startAppAuthPolling();
  }

  public closeResetPasswordAppAuthDialogIfOpen(): void {
    if (this.appAuthResetPasswordDialogRef?.getState() === MatDialogState.OPEN) {
      this.appAuthResetPasswordDialogRef.close();
    }
  }

  /**
   * Browser: Back button + "Not Now? Send Me a Code" footer (activation requires the mobile app).
   * iOS/Android: Confirm / Cancel with the same "Not Now? Send Me a Code" footer.
   */
  public showNeedsActivateDialog(userId: string = ''): void {
    if (this.needsActivateDialogRef?.getState() === MatDialogState.OPEN) {
      this.needsActivateDialogRef.close();
    }

    const data: AppAuthenticatorNeedsActivateDialogData = {};
    const dialogConfig = new MatDialogConfig<AppAuthenticatorNeedsActivateDialogData>();
    dialogConfig.data = data;
    dialogConfig.maxWidth = 600;
    dialogConfig.width = '98%';
    dialogConfig.height = 'auto';
    dialogConfig.panelClass = 'custom-dialog-confirm-messages';
    dialogConfig.disableClose = true;

    this.needsActivateDialogRef = this.srvDialog.open(AppAuthenticatorNeedsActivateComponent, dialogConfig);
    this.needsActivateDialogRef.afterClosed().subscribe((choice) => {
      switch (choice) {
        case 'sendCode':
          this.sendStandardMfaFromAppAuthWait();
          break;
        case 'continue':
          this.beginTransactionalAppAuthActivation();
          this.startAppAuthenticatorActivation(userId, {
            skipPrompt: true,
            keepSessionOnCancel: true,
            onSuccess: () => {
              // After mid-session activation, resume the original MFA challenge
              // on the caller's result subject (not the activation MFA subject).
              const resumeScope = this.scopeBeforeAppActivate ?? this.scope;
              const resumeProps = this.additionalPropsBeforeAppActivate ?? this.additionalProps;
              const resumeResult = this.resultBeforeAppActivate;
              this.clearTransactionalAppAuthActivation();
              if (resumeResult) {
                this.result = resumeResult;
              }
              this.useAppAuthenticatorDevice();
              this.preauth(resumeScope, { ...resumeProps, useAppAuth: true}, true);
            },
          });
          break;
        case 'back':
        default:
          this.cancelAppAuthWait();
          break;
      }
    });
  }

  public closeNeedsActivateDialogIfOpen(): void {
    if (this.needsActivateDialogRef?.getState() === MatDialogState.OPEN) {
      this.needsActivateDialogRef.close();
    }
  }

  /**
   * Lazily resolves AppAuthenticatorService to avoid a circular DI with MfaService.
   */
  private startAppAuthenticatorActivation(
    userId: string,
    options?: { skipPrompt?: boolean; keepSessionOnCancel?: boolean; onSuccess?: () => void }
  ): void {
    const resolvedUserId = userId
      || (this.additionalProps as { userId?: string }).userId
      || this.srvIS.internalSettings$.value?.profile?.username
      || '';

    const srvAppAuth = this.injector.get(AppAuthenticatorService);
    srvAppAuth.activate(resolvedUserId, options).subscribe((res) => {
      if (res == null) {
        return;
      }
      if (res === AppAuthenticatorResult.CANCELLED || res === AppAuthenticatorResult.FAILED) {
        if (options?.keepSessionOnCancel) {
          // Mid-session (e.g. EFT): never log off. MFA cancel may already have cleaned up;
          // avoid a second cancelAppAuthWait after keepSession was cleared (scope is APP_ACTIVATE).
          this.stopAppAuthPolling();
          this.pendingAppAuthResend = false;
          this.suppressAppAuthWaitingDialog = false;
          this.closeAppAuthWaitingDialog();
          this.closeDialogIfOpen();
          this.publishMfaResult(MFAResult.UNVERIFIED);
          this.clearTransactionalAppAuthActivation();
        } else if (this.scope !== MFAScope.LOGIN) {
          this.cancelAppAuthWait();
        }
      }
    });
  }

  private cancelAppAuthWait(): void {
    this.injector.get(AppAuthenticatorService).clearPendingLoginKeyPair();
    this.stopAppAuthPolling();
    this.pendingAppAuthResend = false;
    this.suppressAppAuthWaitingDialog = false;
    const shouldLogoff = this.shouldLogoffOnMfaCancelOrError();
    this.closeAppAuthWaitingDialog();
    this.closeResetPasswordAppAuthDialogIfOpen();
    this.closeDialogIfOpen();
    this.publishMfaResult(MFAResult.UNVERIFIED);
    this.clearTransactionalAppAuthActivation();
    if (shouldLogoff) {
      this.srvUser.logoff();
    }
  }

  public closeDialogIfOpen(): void {
    if (this.dialogRef?.getState() == MatDialogState.OPEN) {
      this.dialogRef.close();
    }
  }

  private readonly setDialogConfig = (data: MFADialogConfig) => {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = data;
    dialogConfig.maxWidth = 600;
    dialogConfig.panelClass = ['custom-dialog-mfa-messages', new CustomDialogWidthConfig(this.srvRpDesign).getDialogWidthClass({ XS: '98' })];
    dialogConfig.hasBackdrop = false;
    return dialogConfig;
  };

  public showDialog(): Observable<null | undefined> {
    // Close the previous dialog if exist
    if (this.dialogRef?.getState() == MatDialogState.OPEN) {
      this.dialogRef.close();
    }

    let mfaDilogConfig: MFADialogConfig = {
      scope: this.scope,
      status: this.status
    }
    this.dialogRef = this.srvDialog.open(MfaComponent, this.setDialogConfig(mfaDilogConfig))

    return this.dialogRef.afterClosed();
  }

  public getAuthResult(): MEAPIResponse<any> {
    return this.authResult;
  }

  public setAuthResult(authResult: MEAPIResponse<any>) {
    this.authResult = authResult;
  }

  auth(userResponse: string, trustDevice: boolean = false): AsyncSubject<boolean> {

    let bean: TfaAuthRequest = {
      token: this.token,
      scope: this.scope,
      userResponse,
      trustDevice
    };
    bean = Object.assign(bean, this.additionalProps);

    let errorTitle = this.translate.instant('MFA_VERIFY.VALIDATION_TITLE_FAIL');
    return this.srvAPI.Post(this.API_VERIFY_MFA, bean, [
      new VerifySuccessHandler(ResponseCode.SUCCESS, this),
      new PasswordExpiryHandler(ResponseCode.MFA_CODE_EXPIRED, this.scope, this, this.srvUser, this.srvError, errorTitle),
      new MECustomTitleErrorResponseHandler(ResponseCode.MAX_TRIES_EXCEEDED, this.srvError, errorTitle),
      new MECustomTitleErrorResponseHandler(ResponseCode.MFA_CODE_INVALID, this.srvError, errorTitle),
      new MECustomTitleErrorResponseHandler(ResponseCode.INVALID_MFA, this.srvError, errorTitle),
      new MECustomTitleErrorResponseHandler(ResponseCode.MFA_ACCESS_DENIED, this.srvError, errorTitle),       // Duo MFA Only

      new ExceedMaxRetryLogoffHandler(ResponseCode.MFA_EXCEED_MAX_RETRY_LOGOFF, this, false),
      new ExceedMaxRetrySuspendHandler(ResponseCode.MFA_EXCEED_MAX_RETRY_SUSPENSE, this,false),

      new PendingTransactionHandler(ResponseCode.PENDING_TRANSACTION, this),
      new PendingTransactionHandler(ResponseCode.PENDING_THRESHOLD_TRANSACTION, this),
      new AccountMonitoringReviewMfaHandler(ResponseCode.ACCOUNT_MONITORING_REVIEW, this),

      new CIFUpdateHandler(ResponseCode.CIF_UPDATE, '/customer-information/update', this, this.router),
      new CIFUpdateHandler(ResponseCode.FORCE_CIF_UPDATE, '/customer-information/forced', this, this.router),

    ]);
  }

  cancel() {
    this.stopAppAuthPolling();
    this.resetPasswordAppAuthNotificationRequested = false;
    this.pendingAppAuthResend = false;
    this.suppressAppAuthWaitingDialog = false;
    this.closeAppAuthWaitingDialog();
    this.closeAppAuthNoNotificationDialogIfOpen();
    this.closeResetPasswordAppAuthDialogIfOpen();
    this.closeNeedsActivateDialogIfOpen();

    // LOGIN / RESET_PASSWORD / login-time activation: cancel ends the session.
    // Transactional scopes (EFT, etc.) and mid-session activation: only close dialogs.
    const shouldLogoff = this.shouldLogoffOnMfaCancelOrError();
    this.closeDialogIfOpen();
    this.publishMfaResult(MFAResult.UNVERIFIED);
    this.clearTransactionalAppAuthActivation();
    if (shouldLogoff) {
      this.srvUser.logoff();
    }
  }

  /**
   * Reject an App Authenticator push approval request on the trusted device.
   * Success is a no-op here — the caller shows the deny success UI.
   */
  deny(tfaPassphrase?: string): AsyncSubject<boolean> {
    const result = new AsyncSubject<boolean>();
    this.srvSecureStorage.get(SecureStorageType.APP_AUTHENTICATOR_PRIVATE_KEY).subscribe((stored) => {
      const bean: TfaAuthRequest = {
        token: this.token,
        scope: this.scope,
        userResponse: tfaPassphrase,
      };
      if (stored) {
        const credential: AppAuthenticatorStoredCredential = JSON.parse(stored);
        bean.userId = credential.userId;
      }
      this.srvAPI.Post(this.API_APP_AUTH_REJECT, bean, [
        new MEDoNothingResponseHandler(ResponseCode.SUCCESS),
      ]).subscribe({
        next: (ok) => {
          result.next(ok);
          result.complete();
        },
        error: (err) => result.error(err),
      });
    });
    return result;
  }

  logoff(message: string) {
    this.srvError.showDialog('Error', [message])
      .subscribe(() => {
        // Server signalled max-retry logoff; still keep session for transactional MFA (e.g. EFT).
        const shouldLogoff = this.shouldLogoffOnMfaCancelOrError();
        this.stopAppAuthPolling();
        this.closeAppAuthWaitingDialog();
        this.closeResetPasswordAppAuthDialogIfOpen();
        this.closeDialogIfOpen();
        this.publishMfaResult(MFAResult.TOO_MANY);
        this.clearTransactionalAppAuthActivation();
        if (shouldLogoff) {
          this.srvUser.logoff();
        }
      });
  }
}

class MFARegistrationHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private srvConfirm: MeConfirmDialogService, private srvUser: UserService, private translate: TranslateService) { }

  handle(response: MEAPIResponse<TokenResponseBean>): boolean | MEError {

    let confirmDialogConfig: ConfirmDialogConfig = {
      dialogType: DialogType.CONFIRM,
      title: response.message,
      message: this.translate.instant('MFA_VERIFY.EXTERNAL_MFA_REGISTRATION_MESSAGE'),
      confirmButtonText: this.translate.instant('CONTINUE'),
      cancelButtonText: this.translate.instant('CANCEL')
    }

    this.srvConfirm.showDialog(confirmDialogConfig).subscribe((isProceedRegistration) => {
      this.srvUser.logoff(true).subscribe(() => {
        if (isProceedRegistration) {
          window.location.href = response.entity.registrationUrl!;
        } else {
          window.location.reload();
        }
      });
    });

    return false;
  }
}

class GenerateLoadHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private srvMfa: MfaService) { }

  handle(response: MEAPIResponse<TokenResponseBean>): boolean | MEError {
    this.srvMfa.token = response.entity.token;
    this.srvMfa.status = this.code;
    return true;
  }
}

class AppAuthenticatorRequiredHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private readonly srvMfa: MfaService) { }

  handle(response: MEAPIResponse<TokenResponseBean>): boolean | MEError {
    this.srvMfa.token = response.entity.token;
    this.srvMfa.status = this.code;
    this.srvMfa.handleAppAuthenticatorRequired();
    return false;
  }
}

class AppAuthenticatorSelfAuthHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private readonly srvMfa: MfaService) { }

  handle(response: MEAPIResponse<TokenResponseBean>): boolean | MEError {
    this.srvMfa.token = response.entity.token;
    this.srvMfa.status = this.code;
    this.srvMfa.handleAppAuthenticatorSelfAuth(response.entity.actionResult ?? '');
    return false;
  }
}

class AppAuthenticatorNeedsActivationHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private readonly srvMfa: MfaService) { }

  handle(response: MEAPIResponse<TokenResponseBean>): boolean | MEError {
    if (response.entity?.token) {
      this.srvMfa.token = response.entity.token;
    }
    this.srvMfa.status = this.code;
    this.srvMfa.handleAppAuthenticatorNeedsActivation(response.entity?.userId ?? '');
    return false;
  }
}

class AppAuthInProgressHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private readonly srvMfa: MfaService) { }

  handle(_response: MEAPIResponse<TokenResponseBean>): boolean | MEError {
    this.srvMfa.scheduleNextAppAuthPoll();
    return false;
  }
}

class AppAuthSuccessHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private readonly srvMfa: MfaService) { }

  handle(response: MEAPIResponse<TokenResponseBean>): boolean | MEError {
    this.srvMfa.completeAppAuthSuccess(response);
    return true;
  }
}

class AppAuthRejectedHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private readonly srvMfa: MfaService) { }

  handle(_response: MEAPIResponse<TokenResponseBean>): boolean | MEError {
    this.srvMfa.stopAppAuthPolling();
    this.srvMfa.closeAppAuthWaitingDialog();
    this.srvMfa.showAppAuthRejectedDialog();
    return false;
  }
}

class AppAuthExpiredHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private srvMfa: MfaService) { }

  handle(_response: MEAPIResponse<TokenResponseBean>): boolean | MEError {
    this.srvMfa.stopAppAuthPolling();
    this.srvMfa.closeAppAuthWaitingDialog();
    this.srvMfa.showAppAuthExpiredDialog();
    return false;
  }
}

class AppAuthApproveExpiredHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private readonly srvMfa: MfaService) { }

  handle(_response: MEAPIResponse<TokenResponseBean>): boolean | MEError {
    this.srvMfa.handlePushApprovalExpired();
    return false;
  }
}

class VerifySuccessHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private readonly srvMfa: MfaService) { }
  handle(response: MEAPIResponse<TokenResponseBean>): boolean | MEError {
    this.srvMfa.setAuthResult(response);
    this.srvMfa.closeDialogIfOpen();
    this.srvMfa.result.next(MFAResult.VERIFIED);
    return true;
  }
}

/** 4780 on MFA verify: close OTP / push-wait, then let the payment showMfa open the review dialog. */
class AccountMonitoringReviewMfaHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private readonly srvMfa: MfaService) { }

  handle(response: MEAPIResponse<TokenResponseBean>): boolean | MEError {
    this.srvMfa.stopAppAuthPolling();
    this.srvMfa.closeAppAuthWaitingDialog();
    this.srvMfa.setAuthResult(response);
    this.srvMfa.closeDialogIfOpen();
    this.srvMfa.publishMfaResult(MFAResult.ACCOUNT_MONITORING_REVIEW);
    return false;
  }
}

class PendingTransactionHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private readonly srvMfa: MfaService) { }
  handle(response: MEAPIResponse<TokenResponseBean>): boolean | MEError {
    this.srvMfa.setAuthResult(response);
    this.srvMfa.closeDialogIfOpen();
    this.srvMfa.result.next(MFAResult.VERIFIED);
    return true;
  }
}

class PasswordExpiryHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private readonly scope: MFAScope, private readonly srvMfa: MfaService, private readonly srvUser: UserService, private readonly srvError: MEErrorService, private readonly title: string) { }

  handle(response: MEAPIResponse<any>): boolean {
    this.srvError.showDialog(this.title, [{
      code: response.status.code,
      message: response.status.message,
      display: MEErrorType.Popup,
      url: ''
    }]).subscribe(() => {
      const shouldLogoff = this.srvMfa.shouldLogoffOnMfaCancelOrError();
      this.srvMfa.closeDialogIfOpen();
      this.srvMfa.publishMfaResult(MFAResult.EXPIRED);
      this.srvMfa.clearTransactionalAppAuthActivation();

      if (shouldLogoff) {
        this.srvUser.logoff().subscribe(() => {
          window.location.reload();
        });
      }
    });
    return true;
  }
}

class ExceedMaxRetryLogoffHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private readonly srvMfa: MfaService, private readonly useAppAuthenticator: boolean) { }
  handle(entity: MEAPIResponse<TokenResponseBean>): boolean | MEError {
    this.srvMfa.logoff(entity.message);
    return !this.useAppAuthenticator;
  }
}

class ExceedMaxRetrySuspendHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private readonly srvMfa: MfaService, private readonly useAppAuthenticator: boolean) { }
  handle(entity: MEAPIResponse<TokenResponseBean>): boolean | MEError {
    this.srvMfa.logoff(entity.message);
    return !this.useAppAuthenticator;
  }
}

class CIFUpdateHandler implements MEEntityAPIResponseHandler<TokenResponseBean> {
  constructor(public code: ResponseCode, private readonly url: string, private readonly srvMfa: MfaService, private readonly router: Router) { }
  handle(response: MEAPIResponse<TokenResponseBean>): boolean | MEError {
    this.router.navigate([this.url]);
    this.srvMfa.setAuthResult(response);
    this.srvMfa.closeDialogIfOpen();
    this.srvMfa.result.next(MFAResult.VERIFIED);
    return true;
  }
}
